import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import type { User } from '../types';
import { getPersonalHouseholdId, resolveActiveHouseholdId } from './householdUtils';
import { getTranslation } from './translations';
import { isGuidedUser } from './accountMode';

export async function ensurePersonalHousehold(user: User): Promise<{ user: User; activeHouseholdId: string }> {
    if (isGuidedUser(user)) {
        return {
            user,
            activeHouseholdId: resolveActiveHouseholdId(user),
        };
    }

    const personalId = getPersonalHouseholdId(user.uid);
    const householdRef = doc(db, 'households', personalId);
    const householdSnap = await getDoc(householdRef);

    if (!householdSnap.exists()) {
        await setDoc(householdRef, {
            id: personalId,
            name: getTranslation(user.language, 'personalHouseholdName'),
            ownerId: user.uid,
            members: [user.uid],
            memberRoles: { [user.uid]: 'owner' },
            residentsCount: 1,
            adultsCount: 1,
            childrenCount: 0,
            visibleMealTypes: ['Breakfast', 'Lunch', 'Dinner'],
        });
    } else {
        const data = householdSnap.data();
        const members: string[] = data.members || [];
        if (!members.includes(user.uid)) {
            await updateDoc(householdRef, {
                members: [...members, user.uid],
                memberRoles: {
                    ...(data.memberRoles || {}),
                    [user.uid]: data.ownerId === user.uid ? 'owner' : 'viewer',
                },
            });
        }
    }

    const userUpdates: Partial<User> = {};
    let nextHouseholds = user.households || [];
    if (!nextHouseholds.includes(personalId)) {
        nextHouseholds = [personalId, ...nextHouseholds];
        userUpdates.households = nextHouseholds;
    }

    const activeId = user.activeHouseholdId;
    if (
        activeId
        && activeId !== personalId
        && !nextHouseholds.includes(activeId)
    ) {
        const activeSnap = await getDoc(doc(db, 'households', activeId));
        if (activeSnap.exists() && (activeSnap.data().members || []).includes(user.uid)) {
            nextHouseholds = [...nextHouseholds, activeId];
            userUpdates.households = nextHouseholds;
        }
    }

    const resolvedActive = resolveActiveHouseholdId({
        ...user,
        households: nextHouseholds,
    });

    if (user.activeHouseholdId !== resolvedActive) {
        userUpdates.activeHouseholdId = resolvedActive;
    }

    if (Object.keys(userUpdates).length > 0) {
        await updateDoc(doc(db, 'users', user.uid), userUpdates);
    }

    return {
        user: { ...user, ...userUpdates, households: nextHouseholds },
        activeHouseholdId: resolvedActive,
    };
}
